// src/pages/Dashboard.jsx
import React, { useEffect, useState } from "react";
import {
  Card,
  Spin,
  Row,
  Col,
  Typography,
  Alert,
  Statistic,
  Space,
  Divider,
  Tag,
  Button,
  Progress,
} from "antd";
import {
  TeamOutlined,
  CarOutlined,
  EuroCircleOutlined,
  CalculatorOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { getPlanningModeLabels } from "../utils/planningMode";
import { euro } from "../utils/tariffs";

const { Title, Text } = Typography;

const COLORS = {
  primary: "#3FB6C2",
  primaryDark: "#1F4E57",
  primaryLight: "#E8F7F9",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingLimits, setLoadingLimits] = useState(true);

  useEffect(() => {
    axios
      .get("/users/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoadingUser(false));

    axios
      .get("/users/me/limits")
      .then((res) => setLimits(res.data))
      .catch(() => setLimits(null))
      .finally(() => setLoadingLimits(false));
  }, []);

  const loading = loadingUser || loadingLimits;
  const labels = getPlanningModeLabels(user?.planningMode);

  const validUntilStr = limits?.subscriptionValidUntil
    ? new Date(limits.subscriptionValidUntil).toLocaleDateString("de-DE")
    : "—";

  const usedPassengers = limits?.usedPassengers ?? 0;
  const usedBuses = limits?.usedBuses ?? 0;

  const effPassengerLimit = limits?.effectivePassengerLimit ?? 0;

  const paxLimitReached = !!limits?.passengerLimitReached;

  const subscriptionExpired = !!limits?.subscriptionExpired;

  const billablePassengers = Math.max(effPassengerLimit, 0);
  const pricingQuote = limits?.pricingQuote?.pricingMode === "FLAT" ? limits.pricingQuote : null;
  const annualPrice = pricingQuote?.annualPriceNet ?? null;
  const billingPeriod = limits?.billingPeriod;
  const monthlyPayment = billingPeriod === "MONTHLY";
  const billingLabel = monthlyPayment ? "Monatlich zahlen" : billingPeriod === "ANNUAL" ? "Jährlich im Voraus" : "Zahlungsweise noch nicht hinterlegt";
  const selectedPrice = monthlyPayment ? pricingQuote?.monthlyPriceNet ?? null : annualPrice;
  const priceTypeLabel = "Individueller Kundenpreis";
  const usagePercent = effPassengerLimit > 0
    ? Math.min(100, Math.round((usedPassengers / effPassengerLimit) * 100))
    : 0;

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      <h2 className="app-page-title">
        Willkommen{user && user.email ? `, ${user.email}` : ""}!
      </h2>

      <Text style={{ fontSize: 16, color: COLORS.muted }}>
        Ihre aktuelle Nutzung, Kapazitäten und Jahreslizenz im Überblick.
      </Text>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={14}>
          <Card
            variant="borderless"
            style={{
              background: `linear-gradient(135deg, ${COLORS.primaryLight} 0%, #FFFFFF 58%)`,
              borderRadius: 18,
              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 10px 28px rgba(31, 78, 87, 0.08)",
              minHeight: 230,
            }}
          >
            {loading ? (
              <Spin />
            ) : !user || !limits ? (
              <span style={{ color: "red" }}>
                Daten konnten nicht geladen werden.
              </span>
            ) : (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                  <div
                    style={{
                      fontSize: 34,
                      color: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 60,
                      height: 60,
                      borderRadius: 18,
                      background: COLORS.primary,
                      boxShadow: "0 8px 20px rgba(63, 182, 194, 0.35)",
                    }}
                  >
                    <EuroCircleOutlined />
                  </div>

                  <div style={{ minWidth: 220 }}>
                    <div
                      style={{
                        fontSize: 20,
                        marginBottom: 8,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 8,
                        color: COLORS.text,
                      }}
                    >
                      <b>Abrechnung:</b>

                      <Tag
                        style={{
                          background: COLORS.primary,
                          color: "#FFFFFF",
                          border: "none",
                          fontSize: 15,
                          padding: "3px 16px",
                          borderRadius: 999,
                          fontWeight: 700,
                        }}
                      >
                        SMART ROUTING · {billablePassengers} FAHRGÄSTE
                      </Tag>
                    </div>

                    <div style={{ fontSize: 16, color: COLORS.muted }}>
                      <b style={{ color: COLORS.text }}>Vertragsbeginn:</b>{" "}
                      {limits?.subscriptionValidFrom
                        ? new Date(limits.subscriptionValidFrom).toLocaleDateString("de-DE")
                        : "—"}
                    </div>
                    <div style={{ fontSize: 16, color: COLORS.muted }}>
                      <b style={{ color: COLORS.text }}>Gültig bis:</b>{" "}
                      {validUntilStr}
                    </div>

                    {subscriptionExpired && (
                      <Alert
                        style={{ marginTop: 12, maxWidth: 420 }}
                        type="error"
                        showIcon
                        message="Ihr Zugang ist abgelaufen."
                        description="Bitte verlängern, um weiterhin planen zu können."
                      />
                    )}
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 22,
                    fontSize: 16,
                    lineHeight: 1.6,
                    color: COLORS.text,
                  }}
                >
                  <div>
                    <b>{selectedPrice !== null ? euro.format(selectedPrice) : "Noch nicht vereinbart"}</b>{" "}
                    {selectedPrice !== null && <span style={{ color: COLORS.muted }}>/ {monthlyPayment ? "Monat" : "Jahr"} netto</span>}
                  </div>

                  <div style={{ marginTop: 6, fontWeight: 600, color: COLORS.primaryDark }}>{billingLabel}</div>
                  <div style={{ marginTop: 6, color: COLORS.muted }}>
                    {"Individueller Kundenpreis"} für {billablePassengers} Fahrgäste – alle Funktionen inklusive.
                  </div>
                  <div style={{ marginTop: 4, color: COLORS.muted, fontSize: 13 }}>
                    Preisart: {priceTypeLabel}
                  </div>

                  <div style={{ marginTop: 4, color: COLORS.muted, fontSize: 13 }}>
                    Nettopreis; zuzüglich gesetzlicher Umsatzsteuer, sofern sie anfällt.
                  </div>

                  <div style={{ marginTop: 18 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span>Kapazitätsnutzung</span>
                      <b>{usedPassengers} von {effPassengerLimit}</b>
                    </div>
                    <Progress percent={usagePercent} showInfo={false} strokeColor={COLORS.primary} />
                  </div>
                </div>
              </>
            )}
          </Card>

          


        </Col>

        <Col xs={24} lg={10}>
          <Card
            variant="borderless"
            style={{
              borderRadius: 18,
              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 10px 28px rgba(31, 78, 87, 0.08)",
              minHeight: 230,
            }}
          >
            {loading ? (
              <Spin />
            ) : !limits ? (
              <span style={{ color: "red" }}>
                Limits konnten nicht geladen werden.
              </span>
            ) : (
              <>
                <Title level={5} style={{ marginTop: 0, color: COLORS.text }}>
                  Nutzung & Kapazitäten
                </Title>

                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  <Space
                    align="center"
                    style={{ justifyContent: "space-between", width: "100%" }}
                  >
                    <Space>
                      <TeamOutlined style={{ color: COLORS.primary }} />
                      <Text>Passagiere</Text>
                    </Space>

                    <Statistic
                      value={`${usedPassengers} / ${effPassengerLimit}`}
                      valueStyle={{ fontSize: 18, color: COLORS.text }}
                    />
                  </Space>

                  <Space
                    align="center"
                    style={{ justifyContent: "space-between", width: "100%" }}
                  >
                    <Space>
                      <CarOutlined style={{ color: COLORS.primary }} />
                      <Text>{labels.vehiclePlural}</Text>
                    </Space>

                    <Statistic
                      value={usedBuses}
                      valueStyle={{ fontSize: 18, color: COLORS.text }}
                    />
                  </Space>
                </Space>

                {paxLimitReached && (
                  <Alert
                    style={{ marginTop: 16 }}
                    type="warning"
                    showIcon
                    message="Fahrgastlimit erreicht"
                    description="Bitte zusätzliche Fahrgäste freischalten lassen."
                  />
                )}

                <Divider style={{ margin: "16px 0" }} />

                <section className="calculation-box" aria-label="Kostenübersicht Smart Routing">
                  <div className="calculation-title">
                    <CalculatorOutlined /> Kostenübersicht
                  </div>
                  <div className="calculation-meta">
                    <span>Smart Routing</span>
                    <span><strong>{billablePassengers}</strong> Fahrgäste</span>
                  </div>

                  {billingPeriod !== "MONTHLY" && <div className="payment-option">
                    <div className="payment-option-label">{billingLabel}</div>
                    <div className="payment-option-price">
                      {annualPrice !== null ? euro.format(annualPrice) : "Noch nicht vereinbart"}
                      {annualPrice !== null && <span>netto / Jahr</span>}
                    </div>
                    <div className="payment-option-note">{"Individueller Kundenpreis"}</div>
                    
                  </div>}

                  {monthlyPayment && <div className="payment-option payment-option-monthly">
                    <div className="payment-option-label">Monatlich zahlen </div>
                    <div className="payment-option-price">
                      {selectedPrice != null ? euro.format(selectedPrice) : "Noch nicht vereinbart"}<span>netto / Monat</span>
                    </div>
                    <div className="payment-option-note">{priceTypeLabel}{selectedPrice != null && ` · ${euro.format(selectedPrice * 12)} netto für zwölf Monate`}</div>
                    
                    {limits?.individualContractPrice && <div className="payment-option-note">Ihre individuellen Konditionen werden im Angebot berücksichtigt.</div>}
                  </div>}

                  <div className="calculation-footer">
                    <span>Preisart: <strong>{priceTypeLabel}</strong></span>
                    <span>Jahresvertrag: zwölf Monate Laufzeit.</span>
                  </div>
                </section>

                <Space direction="vertical" size={4}>
                  <Text style={{ color: COLORS.muted }}>
                    Es gelten die für Ihren Account hinterlegten Preise.
                  </Text>
                  <Text style={{ color: COLORS.muted }}>
                    Anzahl der {labels.vehiclePlural}, Nutzer und Standardfunktionen verursachen keine Zusatzkosten.
                  </Text>
                  <Text style={{ color: COLORS.muted }}>
                    Nettopreis; zuzüglich gesetzlicher Umsatzsteuer, sofern sie anfällt.
                  </Text>
                  <Text style={{ color: COLORS.muted }}>
                    Aktive Freischaltung: {effPassengerLimit} Fahrgäste
                  </Text>
                  <Text style={{ color: COLORS.muted, fontSize: 12 }}>
                    Nicht hinterlegte Preise werden individuell vereinbart.
                  </Text>
                  <Button type="primary" icon={<ArrowRightOutlined />} onClick={() => navigate("tariffs")}>
                    Kapazität ändern
                  </Button>
                </Space>
              </>
            )}
          </Card>
        </Col>
      </Row>

      <style>{`
        .calculation-box {
          background: #F8FCFD;
          border: 1px solid ${COLORS.border};
          border-radius: 14px;
          padding: 18px;
          margin-bottom: 14px;
        }
        .calculation-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 16px;
          font-weight: 700;
          color: ${COLORS.primaryDark};
          margin-bottom: 12px;
        }
        .calculation-meta {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 6px 12px;
          color: ${COLORS.muted};
          font-size: 13px;
          padding-bottom: 14px;
        }
        .payment-option {
          padding: 16px 0;
          border-top: 1px solid ${COLORS.border};
        }
        .payment-option-label {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: ${COLORS.text};
        }
        .payment-option-price {
          display: flex;
          align-items: baseline;
          flex-wrap: wrap;
          gap: 4px 8px;
          margin: 4px 0;
          font-size: clamp(22px, 3vw, 28px);
          line-height: 1.35;
          font-weight: 700;
          color: ${COLORS.primaryDark};
          overflow-wrap: anywhere;
        }
        .payment-option-price span {
          font-size: 13px;
          font-weight: 400;
          color: ${COLORS.muted};
          white-space: nowrap;
        }
        .payment-option-note { font-size: 12px; color: ${COLORS.muted}; line-height: 1.6; }
        .payment-surcharge {
          border-radius: 6px;
          background: ${COLORS.primaryLight};
          padding: 2px 7px;
          font-size: 12px;
          color: ${COLORS.primaryDark};
        }
        .calculation-footer {
          display: flex;
          flex-direction: column;
          gap: 4px;
          border-top: 1px solid ${COLORS.border};
          padding-top: 12px;
          font-size: 12px;
          color: ${COLORS.muted};
        }
        .calc-row {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 4px 12px;
          color: ${COLORS.text};
        }

        .price-details { margin: 0 0 14px; color: ${COLORS.muted}; }
        .price-details summary { cursor: pointer; font-weight: 600; margin-bottom: 8px; }
        .price-details .calc-row { padding: 3px 0; font-size: 13px; }
      `}</style>
    </div>
  );
};

export default Dashboard;
